import { Trash2, Lock, LockOpen } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { getTypeLabel, getDisplayName } from "../utils";

interface IntegrationsGridProps {
  sources: any[];
  isDeleting: boolean;
  onSelectSource: (id: number) => void;
  onDelete: (id: string) => void;
  onToggleActive: (data: { sourceId: number; isActive: boolean }) => void;
}

const IntegrationsGrid = ({
  sources,
  isDeleting,
  onSelectSource,
  onDelete,
  onToggleActive,
}: IntegrationsGridProps) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
      {sources.map((source) => (
        <Card
          key={source.id}
          className="flex flex-col cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => onSelectSource(source.id)}
        >
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="text-xs flex-shrink-0 border-gray-300 text-gray-600">
                    {getTypeLabel(source.source_type)}
                  </Badge>
                  <CardTitle className="text-lg truncate">{getDisplayName(source)}</CardTitle>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div title={source.credential_id ? "Credentials attached" : "No credentials"}>
                  {source.credential_id ? (
                    <Lock className="h-4 w-4 text-gray-400" />
                  ) : (
                    <LockOpen className="h-4 w-4 text-gray-300" />
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0 flex-1 flex flex-col">
            <div className="flex-1">
              <div className="space-y-2 mb-3">
                <div className="text-xs text-gray-600">
                  <span className="font-medium">Connected agents:</span> {source.agents?.length ?? 0}
                </div>
                <div className="text-xs text-gray-600">
                  <span className="font-medium">Available resources:</span> {source.capabilities?.resources?.length ?? 0}
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 mt-4">
                <Badge variant="outline" className="text-xs w-fit">
                  {source.last_capabilities_fetch
                    ? `Updated ${new Date(source.last_capabilities_fetch).toLocaleDateString()}`
                    : "Pending"}
                </Badge>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <Switch
                    checked={source.is_active}
                    onCheckedChange={(isActive) =>
                      onToggleActive({ sourceId: source.id, isActive })
                    }
                  />
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-red-600">
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Integration</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete this integration? Agents using this
                          integration will need to be updated.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => onDelete(source.id.toString())}
                          disabled={isDeleting}
                        >
                          {isDeleting ? "Deleting..." : "Delete"}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default IntegrationsGrid;
